/**
 * cart-bundle-remove.js
 *
 * <cart-bundle-remove-button data-keys="k1,k2,k3">
 *
 * Removes every line of one bundle in a single request. A bundle product
 * (product.metafields.custom.bundle_size > 0) is added as one line item per
 * size, all sharing a `_bundle_id`; snippets/cart-bundle-item.liquid collapses
 * them into a single cart row, so the row's remove control has to clear all of
 * them at once. Dawn's own <cart-remove-button> can only do one line.
 *
 * POSTs /cart/update.js rather than repeating /cart/change.js per line: one
 * request instead of N, and it's already the theme's mechanism for multi-line
 * updates (see the cart-note handler in assets/cart.js). The bot-challenge
 * concern documented in assets/cart-form-post.js is specific to /cart/add — the
 * cart page already makes scripted /cart/change.js calls on every quantity
 * change.
 *
 * The page is reloaded on success instead of re-rendering sections in place.
 * That duplicates none of CartItems.getSectionsToRender()'s plumbing, and
 * removal is a rare, deliberate action where a reload is not felt.
 *
 * No build step in this theme, so this is a classic script defining a custom
 * element rather than a module.
 */

(function () {
  'use strict';

  // Pure: turns the comma-separated line keys on the element into the
  // { key: 0 } shape /cart/update.js expects. Empty segments are dropped so a
  // trailing comma can never post a `{'': 0}` entry, which Shopify rejects.
  // Unit-tested in tests/cart-bundle-remove.test.mjs.
  function buildRemovalUpdates(keysAttr) {
    const updates = {};
    String(keysAttr == null ? '' : keysAttr)
      .split(',')
      .map(k => k.trim())
      .filter(Boolean)
      .forEach(key => { updates[key] = 0; });
    return updates;
  }

  // Exported for the node unit tests; a no-op in the browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildRemovalUpdates };
  }

  if (typeof customElements === 'undefined' || customElements.get('cart-bundle-remove-button')) return;

  class CartBundleRemoveButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        event.preventDefault();
        this.removeBundle();
      });
    }

    async removeBundle() {
      if (this._removing) return;

      const updates = buildRemovalUpdates(this.dataset.keys);
      if (Object.keys(updates).length === 0) return;

      this._removing = true;
      this.setAttribute('aria-busy', 'true');

      try {
        const res = await fetch(`${window.routes.cart_update_url}`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ updates }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        window.location.reload();
      } catch (err) {
        console.error('[cart-bundle-remove] could not remove the bundle', err);
        this._removing = false;
        this.removeAttribute('aria-busy');
        const errorEl = this.closest('.cart-item')?.querySelector('.cart-item__error-text');
        if (errorEl) errorEl.textContent = 'Could not remove this bundle. Please try again.';
      }
    }
  }

  customElements.define('cart-bundle-remove-button', CartBundleRemoveButton);
})();
